import React, { FC, Ref, useContext, useState } from 'react'

import { FilePondErrorDescription, FilePondFile } from 'filepond'
import FilePondPluginFileMetadata from 'filepond-plugin-file-metadata'
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import filepondPluginImageSizeMetadata from 'filepond-plugin-image-size-metadata'
import { FilePond, registerPlugin } from 'react-filepond'
import 'filepond/dist/filepond.min.css'
import { Message, Dimmer, Button, Modal, Loader, Grid, List } from 'semantic-ui-react'

import { API_URL } from '../../config/environments'
import { GlobalContext } from '../../contexts/GlobalContext'
import Auth from '../../utils/Auth'

// Plugin registration
registerPlugin(
  filepondPluginImageSizeMetadata,
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize,
  FilePondPluginFileMetadata
)

const DEFAULT_UPLOAD_ERROR = 'The upload could not be completed. Please try again.'
const UPLOAD_TIMEOUT_ERROR =
  'The upload timed out before Studio received a response. Please try again or use the project contributor upload page.'

export const normalizeUploadError = (error: unknown): string => {
  if (!error) return DEFAULT_UPLOAD_ERROR

  if (typeof error === 'string') {
    return /timed?\s*out|timeout/i.test(error) ? UPLOAD_TIMEOUT_ERROR : error
  }

  if (typeof error === 'object') {
    const structuredError = error as { body?: unknown; message?: unknown }
    const bodyMessage = normalizeUploadErrorValue(structuredError.body)
    const errorMessage = bodyMessage || normalizeUploadErrorValue(structuredError.message)

    if (errorMessage) return /timed?\s*out|timeout/i.test(errorMessage) ? UPLOAD_TIMEOUT_ERROR : errorMessage
  }

  return DEFAULT_UPLOAD_ERROR
}

const normalizeUploadErrorValue = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) return value

  if (value && typeof value === 'object') {
    const nestedMessage = (value as { message?: unknown }).message
    if (typeof nestedMessage === 'string' && nestedMessage.trim()) return nestedMessage
  }

  return null
}

const UploaderModal: FC<IProps> = (props) => {
  // Global
  const { isMobile, dispatchGlobalContext } = useContext(GlobalContext)

  // Props
  const { pondRef = null, title, requirements, endpoint, handleUploadSuccess, handleClose } = props

  // State
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Use handle warning to display a message in case the user selects too many videos
  const handleWarning = (error: any, file?: FilePondFile | undefined, status?: any) => {
    if (error?.body === 'Max files') setError(`Too many clips selected (max ${requirements.maxFiles})`)
    else setError(normalizeUploadError(error))
  }

  const handleStartProcessingFiles = () => {
    setError(null)
    setLoading(true)
  }

  const handleError = (error: FilePondErrorDescription, file?: FilePondFile | undefined, status?: any) => {
    if (error) {
      setLoading(false)
      if (error.code === 403) return dispatchGlobalContext({ type: 'show update plan modal' })
      setError(normalizeUploadError(error))
    }
  }

  return (
    <Modal dimmer="blurring" open>
      <Modal.Header>{title}</Modal.Header>
      <Modal.Content>
        <Dimmer active={loading}>
          <Loader>Upload in progress...</Loader>
        </Dimmer>
        <Grid textAlign="center" padded>
          <Grid.Row centered>
            <div className="filepond-wrapper" style={{ width: `${isMobile ? '100%' : '50%'}` }}>
              <FilePond
                ref={pondRef}
                labelIdle='<span class="filepond--label-action"><strong>Tap here</strong> to select from my library</span> '
                allowMultiple={requirements.maxFiles > 1}
                maxFiles={requirements.maxFiles}
                maxFileSize={requirements.maxFileSize}
                acceptedFileTypes={requirements.acceptedFileMimeTypes}
                instantUpload={true}
                onerror={handleError}
                onprocessfilestart={handleStartProcessingFiles}
                onwarning={handleWarning}
                onprocessfiles={handleUploadSuccess}
                server={{
                  url: `${API_URL}/rest/${endpoint}`,
                  timeout: 4 * 60 * 1000 + 55 * 1000, // timeout on the server is 5 mins so just want to have a lesser timeout of 4min and 55 seconds on the front end.
                  headers: { Authorization: `Bearer ${Auth.getToken()}` },
                }}
              />
              {error && <Message error={!!error}>{error}</Message>}
            </div>
          </Grid.Row>
          <Grid.Column width={16}>
            {/* Video Requirements List */}
            <List bulleted horizontal>
              <List.Item>
                Max file size:
                <strong> {requirements.maxFileSize}</strong>
              </List.Item>
              <List.Item>
                Max number of files:
                <strong> {requirements.maxFiles}</strong>
              </List.Item>
              <List.Item>
                Accepted formats:
                <strong> {requirements.acceptedFileTypes.join(', ')}</strong>
              </List.Item>
            </List>
          </Grid.Column>
        </Grid>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose}>Close</Button>
      </Modal.Actions>
    </Modal>
  )
}

interface IProps {
  pondRef?: Ref<FilePond>
  title: string
  requirements: any
  endpoint: string
  handleUploadSuccess: () => void
  handleClose: () => void
}

export default UploaderModal
